import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, TrendingUp, Target, Calendar, Handshake, Megaphone,
  Instagram, Linkedin, Link as LinkIcon, BarChart3, ArrowRight,
  CheckCircle, Clock, DollarSign, UserPlus, RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

import { API_URL } from '../../../config/api';

const pipelineConfig = {
  instagram: { name: 'Prospection Instagram', icon: Instagram, color: 'from-pink-500 to-purple-500', bgColor: 'bg-gradient-to-r from-pink-500 to-purple-500' },
  linkedin: { name: 'LinkedIn Growth', icon: Linkedin, color: 'from-blue-600 to-blue-800', bgColor: 'bg-gradient-to-r from-blue-600 to-blue-800' },
  partenaires: { name: 'Partenaires stratégiques', icon: Handshake, color: 'from-emerald-500 to-teal-600', bgColor: 'bg-gradient-to-r from-emerald-500 to-teal-600' },
  influenceurs: { name: 'Influenceurs BTP', icon: Megaphone, color: 'from-orange-500 to-red-500', bgColor: 'bg-gradient-to-r from-orange-500 to-red-500' },
  salons: { name: 'Salons et événements', icon: Calendar, color: 'from-violet-500 to-purple-600', bgColor: 'bg-gradient-to-r from-violet-500 to-purple-600' },
  affiliation: { name: 'Programme affiliation', icon: LinkIcon, color: 'from-cyan-500 to-blue-500', bgColor: 'bg-gradient-to-r from-cyan-500 to-blue-500' },
};

export default function CommercialDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/commercial/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="commercial-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Direction Commerciale</h1>
          <p className="text-gray-500 mt-1">Pilote ta machine commerciale BatiAssist</p>
        </div>
        <Button onClick={fetchStats} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Prospects</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.total_prospects || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Conversions</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.conversions || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">RDV Pris</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.meetings || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Clients Signés</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.clients_signed || 0}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Pipelines Grid */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Vos Pipelines</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(pipelineConfig).map(([key, config], index) => {
            const Icon = config.icon;
            const count = stats?.pipeline_stats?.[key] || 0;
            
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/admin/commercial/pipeline/${key}`}
                  className="block bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all group"
                  data-testid={`pipeline-card-${key}`}
                >
                  <div className="flex items-start justify-between">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.bgColor}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900">{count}</p>
                      <p className="text-xs text-gray-500">prospects</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {config.name}
                    </h3>
                    <div className="flex items-center gap-1 mt-2 text-sm text-gray-400 group-hover:text-blue-500 transition-colors">
                      <span>Voir le pipeline</span>
                      <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sources */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-bold text-gray-900 mb-4">Leads par source</h3>
          {stats?.by_source?.length > 0 ? (
            <div className="space-y-3">
              {stats.by_source.map((source, index) => (
                <div key={source._id} className="flex items-center justify-between">
                  <span className="text-gray-600 capitalize">{source._id || 'Non défini'}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-gray-100 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${(source.count / stats.total_prospects) * 100}%` }}
                      />
                    </div>
                    <span className="font-semibold text-gray-900 w-8 text-right">{source.count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">Aucune donnée de source</p>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-bold text-gray-900 mb-4">Activité récente</h3>
          {stats?.recent_activity?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_activity.slice(0, 5).map((card) => {
                const config = pipelineConfig[card.pipeline_type];
                const Icon = config?.icon || Users;
                return (
                  <Link 
                    key={card.id}
                    to={`/admin/commercial/pipeline/${card.pipeline_type}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config?.bgColor || 'bg-gray-200'}`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{card.name}</p>
                      <p className="text-xs text-gray-500">{card.company_name}</p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(card.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">Aucune activité récente</p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Prêt à développer BatiAssist ?</h3>
            <p className="text-white/80 mt-1">Ajoute tes premiers prospects dans les pipelines</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/commercial/pipeline/instagram">
              <Button className="bg-white text-blue-600 hover:bg-white/90">
                <Instagram className="w-4 h-4 mr-2" />
                Instagram
              </Button>
            </Link>
            <Link to="/admin/commercial/pipeline/linkedin">
              <Button className="bg-white/20 text-white hover:bg-white/30 border border-white/30">
                <Linkedin className="w-4 h-4 mr-2" />
                LinkedIn
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
